import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { ITagDTO, ITagUsageDTO } from '@modeso/types__aumico-lib-companies-be';
import Debug from 'debug';
import { Observable, finalize } from 'rxjs';
import { AumicoCompaniesConfigService } from '../aumico-companies.config';
const debug = Debug('modeso:aumico-companies:AumicoTagsService');

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
    }),
};

/**
 * REV-22155 — HTTP calls for the tags of the organization (shared by persons and companies),
 * aumico-ms-companies under `/tags`. The FE loads all tags once and resolves the tag
 * ids of persons/companies itself.
 */
@Injectable({
    providedIn: 'root',
})
export class AumicoTagsService {
    private readonly tagsUrl: string;

    constructor(
        private http: HttpClient,
        @Inject(AumicoCompaniesConfigService) private config,
        private coreProvider: AumicoCoreProvider,
    ) {
        this.tagsUrl = `${config.apiGateway}tags`;
    }

    getTags(): Observable<ITagDTO[]> {
        debug('getTags called');
        this.coreProvider.dispatchBlockingRequest('getTags');
        return this.http
            .get<ITagDTO[]>(this.tagsUrl)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getTags')));
    }

    /** An existing name (case-insensitive) answers 409. */
    createTag(name: string): Observable<ITagDTO> {
        debug('createTag called');
        this.coreProvider.dispatchBlockingRequest('createTag');
        return this.http
            .post<ITagDTO>(this.tagsUrl, { name }, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createTag')));
    }

    /** How many companies and persons use the tag — asked before deleting. */
    getTagUsage(id: string): Observable<ITagUsageDTO> {
        debug('getTagUsage called');
        this.coreProvider.dispatchBlockingRequest('getTagUsage');
        return this.http
            .get<ITagUsageDTO>(`${this.tagsUrl}/${id}/usage`)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getTagUsage')));
    }

    /** Deletes the tag and removes it from every person and company; 204 without a body. */
    deleteTag(id: string): Observable<void> {
        debug('deleteTag called');
        this.coreProvider.dispatchBlockingRequest('deleteTag');
        return this.http
            .delete<void>(`${this.tagsUrl}/${id}`, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deleteTag')));
    }
}
